import { FieldLabel } from 'core/components/common/FieldLabel'
import { FormSelect } from 'core/components/common/FormSelect'
import React, { ChangeEvent, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useGetAllTagsByClientAuthorizedQuery } from 'core/api/tag'
import { ITag } from 'core/interfaces/Tag'
import { ReactComponent as TagIcone } from 'core/assets/images/icones/appOption.svg'
import { useNavigate } from 'react-router'
import { ROUTE_PATHS } from 'core/constants/routePath'
import { UserContext } from 'core/contexts/UserContext'
import { FieldError, UseFormRegister } from 'react-hook-form'
import { ApplicationForm } from 'lauria/hooks/useEditApplication'
import { skipToken } from '@reduxjs/toolkit/query'

export const TagSelect: React.FC<{
  selectedTags: { id: number; label: string }[]
  setSelectedTags: any
  boldLabel?: boolean
  displayDefaultOption?: boolean
  optionLink?: boolean
  width?: string
  required?: boolean
  error?: FieldError
  register?: UseFormRegister<ApplicationForm>
}> = ({
  selectedTags,
  setSelectedTags,
  boldLabel = false,
  displayDefaultOption = true,
  optionLink = false,
  width = '[300px]',
  required = false,
  error,
}) => {
  const { t } = useTranslation()
  const { user: currentUser, isSuperAdmin: isCurrentUserSuperAdmin } =
    useContext(UserContext)
  const navigate = useNavigate()
  const [value, setValue] = useState('')
  const [tagOptions, setTagOptions] = useState<
    { value: string; label: string }[]
  >([])

  const idClient = currentUser?.idClient
  const { data: tags, isLoading } = useGetAllTagsByClientAuthorizedQuery(
    idClient ? { idClient } : skipToken
  )
  const getOptionFromTags = (tags: ITag[]) => {
    return tags
      .filter((tag) => tag.actif)
      .map((tag) => {
        return { value: tag.id.toString(), label: tag.nomTag }
      })
  }

  useEffect(() => {
    const options = tags ? getOptionFromTags(tags) : []
    setTagOptions(options)
  }, [tags])
  useEffect(() => {
    if (tags) {
      setTagOptions(
        getOptionFromTags(tags).filter(
          (tag) =>
            !selectedTags.some(
              (selectedTag) => selectedTag.id.toString() === tag.value
            )
        )
      )
    }
  }, [selectedTags])
  const handleTagSelect = (event: ChangeEvent<HTMLSelectElement>) => {
    if (
      !selectedTags.find(
        (tag: { id: number; label: string }) =>
          tag.id === parseInt(event.target.value)
      )
    ) {
      setSelectedTags([
        ...selectedTags,
        {
          id: parseInt(event.target.value),
          label:
            tagOptions?.find((tag) => tag.value === event.target.value)
              ?.label ?? '',
        },
      ])
      setTagOptions(tagOptions.filter((tag) => tag.value != event.target.value))
      setValue('')
    }
  }
  const goToTagAdmin = () => {
    navigate(ROUTE_PATHS.TAG_ADMIN)
  }

  return (
    <div className="md:flex md:flex-col">
      <div className={`md:flex md:flex-row w-${width} h-[45px] items-center`}>
        <FieldLabel
          className={`flex items-center h-full ${boldLabel ? 'font-semibold' : ''}`}
          required={required}
          label={
            optionLink
              ? t('applications.filter.tagLabel')
              : t('applications.form.tagLabel')
          }
        />
        {optionLink && isCurrentUserSuperAdmin && (
          <div className="option-link" onClick={goToTagAdmin}>
            {t('tags.optionLink')}{' '}
            <TagIcone className="ml-2 h-[20px] w-[22px]" />
          </div>
        )}
      </div>
      <FormSelect
        placeholder={t('applications.form.tagPlaceholder')}
        options={tagOptions ?? []}
        fieldName="tags"
        wrapperClassName={`md:w-${width}`}
        wrapperErrorClassName="h-[0px]"
        onChange={(event) => handleTagSelect(event)}
        isLoading={isLoading}
        value={value}
        displayDefaultOption={displayDefaultOption}
        error={error}
      />
    </div>
  )
}
